import React, { useEffect, useState } from 'react';
import AdminShell from './AdminShell';
import { getAdminApartments, createApartment, updateApartment, deleteApartment, reorderApartments } from '../../api/client';
import PhotoUploader from '../../components/PhotoUploader';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const FEATURES = [
  { key: 'wifi', label: 'Wi-Fi', icon: '📶' },
  { key: 'ac', label: 'Кондиционер', icon: '❄️' },
  { key: 'kitchen', label: 'Кухня', icon: '🍳' },
  { key: 'washer', label: 'Стиральная машина', icon: '👕' },
  { key: 'dryer', label: 'Сушильная машина', icon: '🌀' },
  { key: 'dishwasher', label: 'Посудомоечная машина', icon: '🍽️' },
  { key: 'tv', label: 'Телевизор', icon: '📺' },
  { key: 'parking', label: 'Парковка', icon: '🅿️' },
  { key: 'elevator', label: 'Лифт', icon: '🛗' },
  { key: 'balcony', label: 'Балкон', icon: '🌇' },
  { key: 'bath', label: 'Ванна', icon: '🛁' },
  { key: 'shower', label: 'Душ', icon: '🚿' },
  { key: 'toilet_separate', label: 'Раздельный санузел', icon: '🚽' },
  { key: 'iron', label: 'Утюг', icon: '👔' },
  { key: 'hairdryer', label: 'Фен', icon: '💇' },
  { key: 'towels', label: 'Полотенца', icon: '🧴' },
  { key: 'bedding', label: 'Постельное бельё', icon: '🛏️' },
  { key: 'kettle', label: 'Чайник', icon: '☕' },
  { key: 'microwave', label: 'Микроволновка', icon: '📡' },
  { key: 'fridge', label: 'Холодильник', icon: '🧊' },
  { key: 'stove', label: 'Плита', icon: '🔥' },
  { key: 'baby_crib', label: 'Детская кроватка', icon: '👶' },
  { key: 'pets_allowed', label: 'Можно с питомцами', icon: '🐾' },
  { key: 'smoking_allowed', label: 'Можно курить', icon: '🚬' },
];

function SortableApartmentCard({ apt, onEdit, onDelete, onToggleActive }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(apt.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="card" {...attributes}>
      <div style={{ padding: 16, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Drag handle */}
        <div {...listeners} style={{ cursor: 'grab', fontSize: 20, color: 'var(--gray-400)', padding: '0 4px', touchAction: 'none' }} title="Перетащите для изменения порядка">
          ⠿
        </div>

        {apt.photos?.[0] && (
          <img src={apt.photos[0]} alt={apt.name}
            style={{ width: 80, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
        )}

        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <h3 style={{ fontWeight: 700, fontSize: 15 }}>{apt.name}</h3>
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            {apt.address && <span>📍 {apt.address} · </span>}
            <span>💰 {new Intl.NumberFormat('ru-RU').format(apt.price_per_night_rub)} ₽/ночь</span>
            {apt.area_sqm && <span> · 📐 {apt.area_sqm} м²</span>}
          </div>
        </div>

        {/* Active toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0 }}>
          <div
            onClick={(e) => { e.preventDefault(); onToggleActive(apt); }}
            style={{
              width: 44, height: 24, borderRadius: 12,
              background: apt.is_active ? 'var(--primary)' : 'var(--gray-300)',
              position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: '50%', background: 'white',
              position: 'absolute', top: 2,
              left: apt.is_active ? 22 : 2,
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </div>
          <span style={{ fontSize: 12, color: apt.is_active ? 'var(--primary)' : 'var(--gray-400)', fontWeight: 600 }}>
            {apt.is_active ? 'Активна' : 'Скрыта'}
          </span>
        </label>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className="btn btn-secondary" style={{ fontSize: 13, padding: '6px 12px' }} onClick={() => onEdit(apt)}>
            ✏️
          </button>
          <button className="btn" style={{ fontSize: 13, padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' }}
            onClick={() => onDelete(apt)}>
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminApartments() {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    getAdminApartments().then(setApartments).finally(() => setLoading(false));
  }, []);

  function parseFeatures(apt) {
    let f = apt.features;
    if (typeof f === 'string') { try { f = JSON.parse(f); } catch { f = {}; } }
    return f || {};
  }

  function startEdit(apt) {
    setEditing(apt.id);
    setForm({
      name: apt.name,
      address: apt.address || '',
      description: apt.description || '',
      max_guests: apt.max_guests,
      price_per_night_rub: apt.price_per_night_rub,
      area_sqm: apt.area_sqm || '',
      rooms: apt.rooms || '',
      bedrooms: apt.bedrooms || '',
      beds: apt.beds || '',
      floor: apt.floor || '',
      total_floors: apt.total_floors || '',
      features: parseFeatures(apt),
      photos: apt.photos || [],
      is_active: apt.is_active,
    });
  }

  function startNew() {
    setEditing('new');
    setForm({
      name: '', address: '', description: '', max_guests: 2, price_per_night_rub: 3000,
      area_sqm: '', rooms: '', bedrooms: '', beds: '', floor: '', total_floors: '',
      features: {}, photos: [], is_active: true,
    });
  }

  async function handleToggleActive(apt) {
    try {
      const updated = await updateApartment(apt.id, { is_active: !apt.is_active });
      setApartments(a => a.map(x => x.id === apt.id ? updated : x));
    } catch (e) {
      alert('Ошибка: ' + (e.response?.data?.error || e.message));
    }
  }

  async function handleDelete(apt) {
    if (!window.confirm(`Удалить "${apt.name}"?`)) return;
    try {
      await deleteApartment(apt.id);
      setApartments(a => a.filter(x => x.id !== apt.id));
    } catch (e) {
      alert('Ошибка: ' + (e.response?.data?.error || e.message));
    }
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = apartments.findIndex(a => String(a.id) === active.id);
    const newIndex = apartments.findIndex(a => String(a.id) === over.id);
    const newOrder = arrayMove(apartments, oldIndex, newIndex);
    setApartments(newOrder);

    try {
      await reorderApartments(newOrder.map(a => a.id));
    } catch (e) {
      // Revert on error
      getAdminApartments().then(setApartments);
    }
  }

  function toggleFeature(key) {
    setForm(f => ({ ...f, features: { ...f.features, [key]: !f.features[key] } }));
  }

  async function save() {
    setSaving(true);
    try {
      const activeFeatures = Object.entries(form.features)
        .filter(([, v]) => v)
        .map(([k]) => FEATURES.find(f => f.key === k)?.label || k);

      const payload = {
        name: form.name, address: form.address, description: form.description,
        max_guests: Number(form.max_guests) || 2,
        price_per_night_rub: Number(form.price_per_night_rub) || 0,
        area_sqm: Number(form.area_sqm) || null,
        rooms: Number(form.rooms) || null,
        bedrooms: Number(form.bedrooms) || null,
        beds: Number(form.beds) || null,
        floor: Number(form.floor) || null,
        total_floors: Number(form.total_floors) || null,
        features: form.features,
        amenities: activeFeatures,
        photos: form.photos,
        is_active: form.is_active ? true : false,
      };

      if (editing === 'new') {
        const created = await createApartment(payload);
        setApartments(a => [...a, created]);
      } else {
        const updated = await updateApartment(editing, payload);
        setApartments(a => a.map(x => x.id === editing ? updated : x));
      }
      setEditing(null);
    } catch (e) {
      alert('Ошибка: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell title="Квартиры">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <p style={{ fontSize: 13, color: 'var(--gray-400)' }}>Перетащите карточки для изменения порядка</p>
        <button className="btn btn-primary" onClick={startNew}>+ Добавить</button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={apartments.map(a => String(a.id))} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {apartments.map(apt => (
                <SortableApartmentCard
                  key={apt.id}
                  apt={apt}
                  onEdit={startEdit}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Edit modal */}
      {editing && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div className="card" style={{ width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
            <h2 style={{ fontWeight: 700, marginBottom: 20, fontSize: 20 }}>
              {editing === 'new' ? 'Новая квартира' : 'Редактировать'}
            </h2>

            <div className="form-group">
              <label className="form-label">Название *</label>
              <input className="form-input" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Студия на Невском" />
            </div>
            <div className="form-group">
              <label className="form-label">Адрес</label>
              <input className="form-input" value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="ул. Примерная, д. 1" />
            </div>
            <div className="form-group">
              <label className="form-label">Описание</label>
              <textarea className="form-input" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
            </div>

            <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, marginTop: 8 }}>Параметры</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              {[
                ['Цена/ночь, ₽', 'price_per_night_rub', '3000'],
                ['Макс. гостей', 'max_guests', '2'],
                ['Площадь, м²', 'area_sqm', '45'],
                ['Комнат', 'rooms', '2'],
                ['Спален', 'bedrooms', '1'],
                ['Кроватей', 'beds', '2'],
                ['Этаж', 'floor', '3'],
                ['Этажей всего', 'total_floors', '9'],
              ].map(([label, key, ph]) => (
                <div key={key}>
                  <label style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
                  <input type="number" className="form-input" style={{ padding: '8px 12px' }}
                    value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={ph} />
                </div>
              ))}
            </div>

            <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Удобства</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 20 }}>
              {FEATURES.map(f => (
                <label key={f.key} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  background: form.features?.[f.key] ? 'var(--primary-light)' : 'transparent',
                  border: form.features?.[f.key] ? '1px solid var(--primary)' : '1px solid transparent',
                  transition: 'all 0.15s', fontSize: 13,
                }}>
                  <input type="checkbox" checked={!!form.features?.[f.key]} onChange={() => toggleFeature(f.key)}
                    style={{ width: 16, height: 16, accentColor: 'var(--primary)' }} />
                  <span>{f.icon} {f.label}</span>
                </label>
              ))}
            </div>

            <div className="form-group">
              <label className="form-label">Фотографии</label>
              <PhotoUploader photos={form.photos || []} onChange={urls => setForm(f => ({ ...f, photos: urls }))} />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: 'var(--primary)' }} />
                Активна (показывается гостям)
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Отмена</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 18, height: 18 }} /> : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}